import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Icons } from '@/theme/Images'
import Contract from './contract'
import { fetchRewardsClass } from '@/api/rewards-class'
import { ETDate } from '@/utils/et-date'

const ContractHeader = ({ contractNumber, onDelete, canDelete }) => (
  <div className="w-full flex items-center justify-between">
    <span
      className="font-semibold"
      style={{
        color: '#101828',
        fontSize: '16px',
      }}
    >
      Contract {contractNumber}
    </span>
    {canDelete && (
      <button
        type="button"
        onClick={onDelete}
        className="bg-transparent border-none p-0 cursor-pointer hover:opacity-80 transition-opacity"
        style={{
          color: 'var(--Brand-700, #467083)',
          fontSize: '14px',
          fontWeight: '600',
          textDecoration: 'underline',
          textUnderlinePosition: 'from-font',
        }}
      >
        Delete Contract
      </button>
    )}
  </div>
)

ContractHeader.propTypes = {
  contractNumber: PropTypes.number,
  onDelete: PropTypes.func,
  canDelete: PropTypes.bool,
}

const ContractGroup = ({ contracts, onContractsChange, errors = {} }) => {
  // State for reward classes
  const [rewardClasses, setRewardClasses] = useState([])

  // Default contract values
  const getDefaultContract = (id = 1) => ({
    id,
    startDate: ETDate.today().toISO(),
    endDate: null,
    rewardClass: 'IN_NETWORK',
    isNew: true, // Mark as new contract
  })

  // Counter for generating unique contract IDs
  const [nextContractId, setNextContractId] = useState(() => Math.max(...contracts.map(c => c.id), 0) + 1)

  // Fetch reward classes on component mount
  useEffect(() => {
    const fetchRewardClasses = async () => {
      try {
        const response = await fetchRewardsClass(1, 100) // Get first 100 reward classes
        if (response && response.data) {
          setRewardClasses(response.data)
        }
      } catch (error) {
        console.error('Failed to fetch reward classes:', error)
      }
    }
    fetchRewardClasses()
  }, [])

  // Contract management functions
  const addContract = () => {
    const newContract = getDefaultContract(nextContractId)
    const updatedContracts = [...contracts, newContract]
    setNextContractId(prev => prev + 1)
    onContractsChange(updatedContracts)
  }

  const updateContract = (contractId, field, value) => {
    const updatedContracts = contracts.map(contract =>
      contract.id === contractId ? { ...contract, [field]: value } : contract
    )
    onContractsChange(updatedContracts)
  }

  const deleteContract = contractId => {
    if (contracts.length <= 1) return // Don't delete if only one contract
    const updatedContracts = contracts.filter(contract => contract.id !== contractId)
    onContractsChange(updatedContracts)
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div
        className="w-full flex items-center"
        style={{
          background: 'var(--Light-Background, #F5F7F8)',
          height: '28px',
          padding: '6px 20px',
          gap: '6px',
          color: '#667085',
          fontSize: '12px',
        }}
      >
        <span className="font-medium">CONTRACTS</span>
      </div>
      <div className="px-6 py-4">
        <div className="flex flex-col gap-6">
          {contracts.map((contract, index) => (
            <div key={contract.id}>
              {contracts.length > 1 && (
                <ContractHeader
                  contractNumber={index + 1}
                  onDelete={() => deleteContract(contract.id)}
                  canDelete={contracts.length > 1 && !contract.savedStartDate}
                />
              )}
              <div className={contracts.length > 1 ? 'pt-4' : ''}>
                <Contract
                  contract={contract}
                  contractFormik={{
                    errors: errors.contracts?.[index] || {},
                  }}
                  updateContract={updateContract}
                  rewardClasses={rewardClasses}
                />
              </div>

              {/* Add Another Contract Button - Only show on the last contract */}
              {index === contracts.length - 1 && contracts.length < 10 && (
                <div className="pt-8">
                  <button
                    type="button"
                    className="w-full py-3 px-4 font-medium bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 ring-offset-background transition-colors duration-200 flex items-center justify-center gap-2 shadow-sm"
                    style={{
                      borderRadius: '8px',
                      border: '1px solid var(--Gray-300, #D0D5DD)',
                      background: 'var(--Base-White, #FFF)',
                      color: 'var(--Gray-700, #344054)',
                      fontSize: '16px',
                    }}
                    onClick={addContract}
                  >
                    <img src={Icons.plusAddPartnerIcon} alt="Add" className="w-5 h-5" />
                    Add Another Contract
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

ContractGroup.propTypes = {
  contracts: PropTypes.array.isRequired,
  onContractsChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
}

export default ContractGroup
